import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hospitalApi, Hospital } from '../../api/hospitalApi';
import httpClient from '../../api/httpClient';

interface PaymentCollection {
  id: string;
  paymentTransactionNumber: string;
  paymentDate: string;
  appointmentNumber: string;
  appointmentDate: string;
  patientName: string;
  serviceName: string;
  paymentMode: string;
  amountReceived: number;
  reference?: string;
  collectedBy?: string;
}

interface CollectionSummary {
  totalCollection: number;
  cash: number;
  card: number;
  upi: number;
  netbanking: number;
  waveOff: number;
  other: number;
  totalTransactions: number;
}

const CollectionReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const reportRef = useRef<HTMLDivElement>(null);
  
  // Initialize dates
  const initializeDefaultDates = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { startOfDay, endOfDay };
  };

  const { startOfDay: defaultFromDate, endOfDay: defaultToDate } = initializeDefaultDates();
  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [collections, setCollections] = useState<PaymentCollection[]>([]);
  const [summary, setSummary] = useState<CollectionSummary>({
    totalCollection: 0,
    cash: 0,
    card: 0,
    upi: 0,
    netbanking: 0,
    waveOff: 0,
    other: 0,
    totalTransactions: 0
  });
  const [loading, setLoading] = useState(false);
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const fetchHospital = async () => {
      try {
        const data = await hospitalApi.getCompanyProfile();
        setHospital(data);
      } catch (error) {
        console.error('Error fetching hospital:', error);
      }
    };
    fetchHospital();
  }, []);

  const fetchCollections = async () => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      const fromDateStr = format(fromDate, 'yyyy-MM-dd');
      const toDateStr = format(toDate, 'yyyy-MM-dd');

      // Fetch all data in parallel
      const [
        paymentReceivesResponse,
        paymentOrdersResponse,
        paymentModesResponse,
        appointmentOrdersResponse
      ] = await Promise.all([
        httpClient.get('/api/payment-receive'),
        httpClient.get('/api/payment-receive-order'),
        httpClient.get('/api/payment-receive-payment-mode'),
        httpClient.get('/api/appointment-orders?limit=10000')
      ]);

      const allPaymentReceives = paymentReceivesResponse.data || [];
      const allPaymentOrders = paymentOrdersResponse.data || [];
      const allPaymentModes = paymentModesResponse.data || [];
      const allAppointmentOrders = appointmentOrdersResponse.data?.data || appointmentOrdersResponse.data || [];

      // Filter payment receives by date range
      const filteredPaymentReceives = allPaymentReceives.filter((pr: any) => {
        if (pr.isDeleted) return false;
        const paymentDate = new Date(pr.paymentDate);
        const from = new Date(fromDateStr);
        const to = new Date(toDateStr);
        to.setHours(23, 59, 59, 999);
        return paymentDate >= from && paymentDate <= to;
      });

      console.log('Filtered payment receives:', filteredPaymentReceives.length);

      const collectionsData: PaymentCollection[] = [];

      for (const paymentReceive of filteredPaymentReceives) {
        // Get payment orders for this payment receive
        const paymentOrders = allPaymentOrders.filter((order: any) => 
          order.paymentReceiveId === paymentReceive.id && !order.isDeleted
        );

        // Get payment modes for this payment receive
        const paymentModes = allPaymentModes.filter((pm: any) => 
          pm.paymentReceiveId === paymentReceive.id && !pm.isDeleted
        );

        console.log(`Payment ${paymentReceive.paymentTransactionNumber}: ${paymentOrders.length} orders, ${paymentModes.length} payment modes`);

        // Get the first order for appointment/patient details (all orders in same payment are for same appointment)
        if (paymentOrders.length === 0) {
          console.log('No payment orders found for payment receive:', paymentReceive.id);
          continue;
        }

        const firstPaymentOrder = paymentOrders[0];
        const appointmentOrder = allAppointmentOrders.find((ao: any) => 
          ao.id === firstPaymentOrder.appointmentOrderId
        );

        if (!appointmentOrder) {
          console.log('Appointment order not found:', firstPaymentOrder.appointmentOrderId);
          continue;
        }

        // Create one row per payment mode for this transaction
        // All payment modes belong to the same transaction/appointment
        if (paymentModes.length > 0) {
          paymentModes.forEach((paymentMode: any) => {
            collectionsData.push({
              id: `${paymentReceive.id}-${paymentMode.id}`,
              paymentTransactionNumber: paymentReceive.paymentTransactionNumber,
              paymentDate: paymentReceive.paymentDate,
              appointmentNumber: appointmentOrder.appointment?.appointmentNumber || 'N/A',
              appointmentDate: appointmentOrder.appointment?.appointmentDate || appointmentOrder.appointment?.date || '',
              patientName: appointmentOrder.appointment?.patient?.fullName || 'Unknown',
              serviceName: 'Multiple Services', // Since one payment can cover multiple services
              paymentMode: paymentMode.mode,
              amountReceived: Number(paymentMode.amount) || 0,
              reference: paymentMode.reference || '',
              collectedBy: paymentReceive.createdBy
            });
          });
        } else {
          // Fallback if no payment modes found
          collectionsData.push({
            id: paymentReceive.id,
            paymentTransactionNumber: paymentReceive.paymentTransactionNumber,
            paymentDate: paymentReceive.paymentDate,
            appointmentNumber: appointmentOrder.appointment?.appointmentNumber || 'N/A',
            appointmentDate: appointmentOrder.appointment?.appointmentDate || appointmentOrder.appointment?.date || '',
            patientName: appointmentOrder.appointment?.patient?.fullName || 'Unknown',
            serviceName: appointmentOrder.item?.itemName || 'Unknown',
            paymentMode: 'CASH',
            amountReceived: paymentReceive.totalPaymentReceived || 0,
            reference: '',
            collectedBy: paymentReceive.createdBy
          });
        }
      }

      console.log('Total collections:', collectionsData.length);
      setCollections(collectionsData);

      // Calculate summary
      const summaryData: CollectionSummary = {
        totalCollection: 0,
        cash: 0,
        card: 0,
        upi: 0,
        netbanking: 0,
        waveOff: 0,
        other: 0,
        totalTransactions: collectionsData.length
      };

      collectionsData.forEach((item: PaymentCollection) => {
        const amount = Number(item.amountReceived) || 0;
        summaryData.totalCollection += amount;
        const mode = item.paymentMode?.toUpperCase().replace(/[\s_-]/g, '') || 'OTHER';
        
        if (mode === 'CASH') {
          summaryData.cash += amount;
        } else if (mode === 'CARD') {
          summaryData.card += amount;
        } else if (mode === 'UPI') {
          summaryData.upi += amount;
        } else if (mode === 'NETBANKING' || mode === 'NETBANKING') {
          summaryData.netbanking += amount;
        } else if (mode === 'WAVEOFF') {
          summaryData.waveOff += amount;
        } else {
          summaryData.other += amount;
        }
      });

      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCollections([]);
      setSummary({
        totalCollection: 0,
        cash: 0,
        card: 0,
        upi: 0,
        netbanking: 0,
        waveOff: 0,
        other: 0,
        totalTransactions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [fromDate, toDate]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Transaction #', 'Appointment #', 'Appointment Date', 'Patient', 'Service', 'Payment Mode', 'Amount', 'Reference'];
    const rows = collections.map(item => [
      format(new Date(item.paymentDate), 'dd/MM/yyyy'),
      item.paymentTransactionNumber,
      item.appointmentNumber,
      format(new Date(item.appointmentDate), 'dd/MM/yyyy'),
      item.patientName,
      item.serviceName,
      item.paymentMode,
      item.amountReceived.toFixed(2),
      item.reference || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `collection_report_${format(fromDate!, 'yyyy-MM-dd')}_to_${format(toDate!, 'yyyy-MM-dd')}.csv`;
    link.click();
    handleExportClose();
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`collection_report_${format(fromDate!, 'yyyy-MM-dd')}_to_${format(toDate!, 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    handleExportClose();
  };

  const paginatedCollections = collections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Collection Report
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCollections}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleExportClick}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
            >
              <MenuItem onClick={exportToCSV}>
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>
        </Paper>

        <div ref={reportRef}>
          {hospital && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold">
                {hospital.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hospital.address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: {hospital.phoneNumber || hospital.mobileNumber} | Email: {hospital.email}
              </Typography>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Collection Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fromDate && toDate && `${format(fromDate, 'dd MMM yyyy')} to ${format(toDate, 'dd MMM yyyy')}`}
              </Typography>
            </Box>
          )}

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    ₹{(summary.totalCollection - summary.waveOff).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Collection
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    (Actual: ₹{summary.totalCollection.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} - Wave Off: ₹{summary.waveOff.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    ₹{summary.cash.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cash
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="info.main">
                    ₹{summary.card.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Card
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="secondary.main">
                    ₹{summary.upi.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    UPI
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="warning.main">
                    ₹{summary.netbanking.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Banking
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="error.main">
                    ₹{summary.waveOff.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wave Off
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="warning.main">
                    {summary.totalTransactions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Transaction #</strong></TableCell>
                  <TableCell><strong>Appointment #</strong></TableCell>
                  <TableCell><strong>Appt Date</strong></TableCell>
                  <TableCell><strong>Patient</strong></TableCell>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell><strong>Payment Mode</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Reference</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedCollections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No collections found for the selected date range
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCollections.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{format(new Date(item.paymentDate), 'dd MMM yyyy')}</TableCell>
                      <TableCell>{item.paymentTransactionNumber}</TableCell>
                      <TableCell>{item.appointmentNumber}</TableCell>
                      <TableCell>{format(new Date(item.appointmentDate), 'dd MMM yyyy')}</TableCell>
                      <TableCell>{item.patientName}</TableCell>
                      <TableCell>{item.serviceName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.paymentMode} 
                          size="small" 
                          color={
                            item.paymentMode === 'CASH' ? 'success' :
                            item.paymentMode === 'CARD' ? 'info' :
                            item.paymentMode === 'UPI' ? 'secondary' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="success.main">
                          ₹{(Number(item.amountReceived) || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.reference || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={collections.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </Box>
    </LocalizationProvider>
  );
};

export default CollectionReport;
